import type { SizeFitGuide } from 'types/capi/shared';

import SizeGuideIcon from '~/assets/icons/SizeGuide';

type Props = {
  sizeFitGuide: SizeFitGuide;
};

const SizeInfo = ({ sizeFitGuide }: Props) => {
  if (!sizeFitGuide) return null;

  return (
    <div
      data-cy="size-info"
      className="flex w-full flex-col items-center justify-center gap-4 rounded-lg bg-grey-tile p-4"
    >
      <Description title={sizeFitGuide.title} text={sizeFitGuide.text} />
      <BarScale adjustment={sizeFitGuide.adjustment} />
    </div>
  );
};

const Description = ({ title, text }: { text: string; title: string }) => {
  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-1">
      <div className="flex items-center justify-center gap-1">
        <SizeGuideIcon height="24" width="24" />
        <p className="font-semibold">{title}</p>
      </div>

      <p className="text-grey-read/75">{text}</p>
    </div>
  );
};

const BarScale = ({ adjustment }: { adjustment: number }) => {
  return (
    <div className="flex w-full flex-col items-center justify-center gap-1.5">
      <div className="flex w-full items-center justify-center gap-1">
        <BarScaleItem isActive={adjustment < -0.5} />
        <BarScaleItem isActive={adjustment === -0.5} />
        <BarScaleItem isActive={adjustment === 0} />
        <BarScaleItem isActive={adjustment === 0.5} />
        <BarScaleItem isActive={adjustment > 0.5} />
      </div>

      <BarScaleLabels adjustment={adjustment} />
    </div>
  );
};

const BarScaleLabels = ({ adjustment }: { adjustment: number }) => {
  return (
    <div className="flex w-full items-center justify-between gap-1 ">
      <BarScaleLabel
        className="text-left"
        isActive={adjustment < 0}
        text="Size down"
      />
      <BarScaleLabel
        className="text-center"
        isActive={adjustment === 0}
        text="True to size"
      />
      <BarScaleLabel
        className="text-right"
        isActive={adjustment > 0}
        text="Size up"
      />
    </div>
  );
};

const BarScaleLabel = ({
  className,
  isActive,
  text
}: {
  className: string;
  isActive: boolean;
  text: string;
}) => {
  const textStyle = isActive ? 'font-semibold text-black' : 'text-grey-divide ';

  return <p className={`${textStyle} ${className} w-1/3`}>{text}</p>;
};

const BarScaleItem = ({ isActive }: { isActive: Boolean }) => {
  const bgColour = isActive ? 'bg-forest' : 'bg-grey-divide';

  return <div className={`h-1.5 grow rounded-xl ${bgColour}`} />;
};

export default SizeInfo;
